<template>
  <div>
    <div class="d-flex align-center mb-1">
      <div class="text-h6 font-weight-bold">Tienda online</div>
      <v-chip
        v-if="estado.tienda"
        :color="estado.publicada ? 'green darken-2' : 'grey darken-2'"
        class="ml-3" dark small label
      >
        {{ estado.publicada ? 'Publicada' : 'Sin publicar' }}
      </v-chip>
      <v-spacer></v-spacer>
      <v-btn v-if="estado.publicada" :href="`https://${tienda.dominio}`" target="_blank" text small>
        <v-icon small class="mr-1">mdi-open-in-new</v-icon>
        {{ tienda.dominio }}
      </v-btn>
    </div>
    <div class="text-body-2 grey--text mb-4">
      Tu catálogo en tu propio dominio, con pedidos online que llegan como presupuestos.
    </div>

    <v-row v-if="loading">
      <v-col class="d-flex justify-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <v-alert v-else-if="!estado.contratada" type="info" outlined>
      La tienda online es un servicio adicional y no está incluida en tu suscripción.
      Pedila a soporte para habilitarla.
    </v-alert>

    <v-card v-else-if="!tienda" outlined class="pa-6 text-center">
      <v-icon x-large class="mb-3">mdi-storefront-outline</v-icon>
      <div class="text-body-1 mb-4">
        Activá la tienda para configurar sucursal, catálogo, marca y dominio.
      </div>
      <v-btn color="primary" :loading="guardando" @click="activar">Activar tienda online</v-btn>
    </v-card>

    <template v-else>
      <v-alert v-if="pendientes.length" type="warning" outlined dense class="mb-4">
        Para publicar la tienda falta: {{ pendientes.join(', ') }}.
      </v-alert>

      <!-- Sucursal y catálogo -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">1. Sucursal y catálogo</v-card-title>
        <v-card-text>
          <div class="text-body-2 grey--text mb-3">
            La tienda vende el stock y los precios de una sucursal, y los pedidos se retiran ahí.
          </div>
          <v-select
            v-model="form.sucursal_uuid"
            :items="sucursales"
            item-text="nombre"
            item-value="sucursal_uuid"
            label="Sucursal"
            outlined dense
          ></v-select>
          <v-btn
            text small
            :disabled="!tienda.sucursal"
            :to="{ name: 'empresa.integracion.categorias', params: { uuid_integracion: tienda.uuid } }"
          >
            <v-icon small class="mr-1">mdi-tag-multiple-outline</v-icon>
            Elegir categorías que se publican
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Marca -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">2. Marca</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.nombre_publico" label="Nombre de la tienda" outlined dense></v-text-field>
          <v-textarea
            v-model="form.descripcion"
            label="Descripción (aparece en la portada)"
            outlined dense rows="2" auto-grow
          ></v-textarea>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.color_primario"
                label="Color de la marca"
                placeholder="#a0358f"
                hint="El color principal de tu logo. Con él se arma la paleta de la tienda."
                persistent-hint
                outlined dense
                :rules="[reglaColor]"
              >
                <template v-slot:prepend-inner>
                  <input v-model="form.color_primario" type="color" class="color-swatch" aria-label="Elegir color" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row dense class="mt-4">
            <v-col cols="12" sm="6">
              <v-img v-if="tienda.logo_url" :src="tienda.logo_url" max-height="64" contain position="left" class="mb-2"></v-img>
              <v-file-input
                v-model="archivos.logo"
                label="Logo"
                accept="image/png,image/jpeg,image/webp"
                prepend-icon="mdi-image-outline"
                outlined dense show-size
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="6">
              <v-img v-if="tienda.banner_url" :src="tienda.banner_url" max-height="64" contain position="left" class="mb-2"></v-img>
              <v-file-input
                v-model="archivos.banner"
                label="Imagen de portada"
                accept="image/png,image/jpeg,image/webp"
                prepend-icon="mdi-panorama-outline"
                outlined dense show-size
              ></v-file-input>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Condiciones de venta -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">3. Entrega y pago</v-card-title>
        <v-card-text>
          <div class="text-body-2 grey--text">El retiro en sucursal está siempre disponible.</div>
          <v-switch v-model="form.envios" label="También hago envíos a domicilio" dense></v-switch>
          <div class="text-body-2 mt-2">Formas de pago que acepto</div>
          <v-checkbox
            v-for="f in FORMAS_PAGO"
            :key="f.value"
            v-model="form.formas_pago"
            :value="f.value"
            :label="f.text"
            :hint="f.hint"
            :persistent-hint="!!f.hint"
            dense hide-details="auto" class="mt-1"
          ></v-checkbox>
        </v-card-text>
      </v-card>

      <!-- Mayoristas -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">4. Venta mayorista</v-card-title>
        <v-card-text>
          <v-switch
            v-model="form.precios_solo_registrados"
            label="Mostrar los precios solo a clientes registrados"
            hint="Sin cuenta se ve el catálogo, pero no los precios ni el botón de compra."
            persistent-hint dense
          ></v-switch>
          <v-text-field
            v-model="form.unidades_por_bulto"
            label="Unidades por bulto"
            type="number" min="2"
            :rules="[reglaBulto]"
            hint="Cada producto se compra de a esta cantidad (ej. 50). La tienda muestra el precio por unidad y no muestra las presentaciones de empaque. Vacío = se vende por unidad."
            persistent-hint outlined dense class="mt-6" style="max-width: 360px"
          ></v-text-field>
          <v-switch
            v-model="form.vender_sin_stock"
            label="Vender sin stock"
            hint="Para lo que se fabrica o repone a pedido: la tienda no muestra &quot;Sin stock&quot; ni limita la cantidad. El stock se descuenta igual al facturar."
            persistent-hint dense class="mt-6"
          ></v-switch>
        </v-card-text>
      </v-card>

      <div class="d-flex justify-end mb-6">
        <v-btn color="primary" :loading="guardando" @click="guardar">Guardar cambios</v-btn>
      </div>

      <!-- Dominio -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">5. Dominio</v-card-title>
        <v-card-text>
          <div class="text-body-2 grey--text mb-3">
            El dominio tiene que ser tuyo (lo comprás en NIC Argentina o cualquier registrador).
            Recomendamos usar <code>www</code>, por ejemplo <code>www.mitienda.com.ar</code>.
          </div>
          <div class="d-flex align-start">
            <v-text-field
              v-model="dominio"
              label="Dominio"
              placeholder="www.mitienda.com.ar"
              outlined dense
              :error-messages="errorDominio"
            ></v-text-field>
            <v-btn
              class="ml-2" color="primary" outlined
              :loading="guardandoDominio"
              :disabled="!dominio || dominio === tienda.dominio"
              @click="guardarDominio"
            >Guardar</v-btn>
          </div>

          <template v-if="tienda.dominio && tienda.dominio_estado !== 'verificado'">
            <div class="text-body-2 mb-2">
              Cargá {{ dns.length > 1 ? 'estos registros' : 'este registro' }} en el DNS de tu dominio
              y después tocá <strong>Verificar</strong>. El cambio puede tardar desde minutos hasta unas horas.
            </div>
            <v-simple-table v-if="dns.length" dense class="mb-3">
              <thead>
                <tr><th>Tipo</th><th>Nombre</th><th>Valor</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in dns" :key="`${r.tipo}-${r.nombre}`">
                  <td>{{ r.tipo }}</td>
                  <td><code>{{ r.nombre }}</code></td>
                  <td><code>{{ r.valor }}</code></td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-btn color="primary" :loading="verificando" @click="verificar">Verificar</v-btn>
          </template>

          <v-alert v-else-if="tienda.dominio" type="success" outlined dense class="mb-0">
            El dominio está verificado y apunta a la tienda.
          </v-alert>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import IntegracionesAPI from '@/api/integraciones'

const FORMAS_PAGO = [
  { value: 'efectivo', text: 'Efectivo' },
  { value: 'transferencia', text: 'Transferencia' },
  {
    value: 'mercado_pago',
    text: 'Mercado Pago',
    hint: 'Requiere tener la cuenta de Mercado Pago conectada en la sucursal.'
  }
]

export default {
  name: 'TiendaOnline',
  data() {
    return {
      FORMAS_PAGO,
      loading: false,
      guardando: false,
      guardandoDominio: false,
      verificando: false,
      estado: { contratada: false, publicada: false, tienda: null },
      sucursales: [],
      form: {},
      archivos: { logo: null, banner: null },
      dominio: '',
      errorDominio: '',
      dns: []
    }
  },
  computed: {
    tienda() {
      return this.estado.tienda
    },
    pendientes() {
      const faltan = []
      if (!this.tienda.sucursal) faltan.push('elegir la sucursal')
      if (!this.tienda.dominio) faltan.push('cargar el dominio')
      else if (this.tienda.dominio_estado !== 'verificado') faltan.push('verificar el dominio')
      return faltan
    }
  },
  methods: {
    reglaBulto(v) {
      return v === '' || v === null || (Number.isInteger(Number(v)) && Number(v) >= 2) || 'Tiene que ser un número entero desde 2, o vacío'
    },
    reglaColor(v) {
      return !v || /^#[0-9a-fA-F]{6}$/.test(v) || 'Tiene que ser un color como #a0358f'
    },
    aviso(text, color = 'success') {
      this.$updateBus.$emit('showSnackBar', {
        show: true,
        button: false,
        color,
        text,
        icon: color === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle',
        timeout: 3000
      })
    },
    // Los 400 del backend son {campo: 'mensaje' | ['mensaje']}
    primerError(error, fallback) {
      const data = error.response && error.response.data
      if (data && typeof data === 'object') {
        const valor = Object.values(data)[0]
        if (valor) return Array.isArray(valor) ? valor[0] : valor
      }
      return fallback
    },
    setEstado(data) {
      this.estado = data
      const t = data.tienda
      if (!t) return
      this.form = {
        nombre_publico: t.nombre_publico,
        descripcion: t.descripcion,
        color_primario: t.color_primario,
        envios: t.envios,
        formas_pago: [...t.formas_pago],
        precios_solo_registrados: t.precios_solo_registrados,
        unidades_por_bulto: t.unidades_por_bulto,
        vender_sin_stock: t.vender_sin_stock,
        sucursal_uuid: t.sucursal ? t.sucursal.uuid : null
      }
      this.dominio = t.dominio || ''
    },
    async fetchEstado() {
      this.loading = true
      try {
        const { data } = await IntegracionesAPI.getTiendaOnline()
        this.setEstado(data)
        if (data.tienda) await this.fetchSucursales()
        // Con el dominio pendiente, se verifica de entrada: además de
        // actualizar el estado, trae los registros DNS que hay que mostrar.
        if (data.tienda && data.tienda.dominio && data.tienda.dominio_estado !== 'verificado') {
          this.verificar({ silencioso: true })
        }
      } catch (e) {
        this.aviso('No se pudo cargar la tienda online', 'error')
      } finally {
        this.loading = false
      }
    },
    async fetchSucursales() {
      const { data } = await IntegracionesAPI.getSucursales(this.tienda.uuid)
      this.sucursales = data
    },
    async activar() {
      this.guardando = true
      try {
        const { data } = await IntegracionesAPI.activarTiendaOnline()
        this.setEstado(data)
        await this.fetchSucursales()
      } catch (e) {
        this.aviso(this.primerError(e, 'No se pudo activar la tienda'), 'error')
      } finally {
        this.guardando = false
      }
    },
    async guardar() {
      if (this.reglaColor(this.form.color_primario) !== true) return
      if (this.reglaBulto(this.form.unidades_por_bulto) !== true) return
      this.guardando = true
      try {
        const payload = { ...this.form }
        if (!payload.sucursal_uuid) delete payload.sucursal_uuid
        const bulto = payload.unidades_por_bulto
        payload.unidades_por_bulto = bulto === '' || bulto === null ? null : Number(bulto)
        await IntegracionesAPI.updateVitrina(this.tienda.uuid, payload)

        // Las imágenes van aparte: multipart no transporta bien la lista de formas de pago.
        if (this.archivos.logo || this.archivos.banner) {
          const fd = new FormData()
          if (this.archivos.logo) fd.append('logo', this.archivos.logo)
          if (this.archivos.banner) fd.append('banner', this.archivos.banner)
          await IntegracionesAPI.updateVitrina(this.tienda.uuid, fd)
          this.archivos = { logo: null, banner: null }
        }
        await this.recargar()
        this.aviso('Cambios guardados. La tienda los muestra en unos minutos.')
      } catch (e) {
        this.aviso(this.primerError(e, 'No se pudieron guardar los cambios'), 'error')
      } finally {
        this.guardando = false
      }
    },
    async guardarDominio() {
      this.guardandoDominio = true
      this.errorDominio = ''
      try {
        await IntegracionesAPI.updateVitrina(this.tienda.uuid, { dominio: this.dominio })
        await this.recargar()
        await this.verificar({ silencioso: true })
      } catch (e) {
        this.errorDominio = this.primerError(e, 'No se pudo guardar el dominio')
      } finally {
        this.guardandoDominio = false
      }
    },
    async verificar({ silencioso = false } = {}) {
      this.verificando = true
      try {
        const { data } = await IntegracionesAPI.verificarDominio(this.tienda.uuid)
        this.dns = data.dns || []
        await this.recargar()
        if (silencioso) return
        if (data.dominio_estado === 'verificado') this.aviso('¡Listo! El dominio apunta a la tienda.')
        else this.aviso('Todavía no vemos el DNS configurado. Probá de nuevo en un rato.', 'warning')
      } catch (e) {
        if (!silencioso) this.aviso(this.primerError(e, 'No se pudo verificar el dominio'), 'error')
      } finally {
        this.verificando = false
      }
    },
    async recargar() {
      const { data } = await IntegracionesAPI.getTiendaOnline()
      this.setEstado(data)
    }
  },
  created() {
    this.fetchEstado()
  }
}
</script>

<style scoped>
.color-swatch {
  width: 24px;
  height: 24px;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
}
</style>
